import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Save, X, Pencil, RotateCcw } from "lucide-react";
import { api, apiError, money, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const monthsAgo = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
    {children}
  </div>
);

export default function Monthly() {
  const { masters } = useData();
  const { can } = useAuth();
  const fileRef = useRef(null);
  const [from, setFrom] = useState(monthsAgo(5));
  const [to, setTo] = useState(monthsAgo(0));
  const [client, setClient] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get("/monthly", {
      params: { start: `${from}-01`, end: `${to}-31`, client_id: client, campaign_id: campaign },
    });
    setRows(data);
  }, [from, to, client, campaign]);

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [load]);

  const save = async (row) => {
    try {
      await api.put("/monthly", {
        period: row.period,
        client_id: row.client_id,
        campaign_id: row.campaign_id,
        clicks: Number(draft.clicks) || 0,
        spend: Number(draft.spend) || 0,
        gst_amount: Number(draft.gst_amount) || 0,
        revenue: Number(draft.revenue) || 0,
        notes: draft.notes || "",
      });
      toast.success("Month updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const reset = async (row) => {
    try {
      await api.delete(`/monthly/${row.period}/${row.client_id}/${row.campaign_id}`);
      toast.success("Back to the rolled-up daily figures");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const exportXlsx = () => {
    const token = localStorage.getItem("adsdesk_token");
    const url = `${API}/export/monthly?start=${from}-01&end=${to}-31&client_id=${client}&campaign_id=${campaign}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r)))
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `monthly-sheet-${from}_${to}.xlsx`;
        a.click();
        toast.success("Excel downloaded");
      })
      .catch(() => toast.error("Could not export"));
  };

  const importXlsx = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/monthly", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.saved} of ${data.rows} rows imported`);
      if (data.errors?.length) toast.error(data.errors[0]);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const totals = rows.reduce(
    (a, r) => ({
      clicks: a.clicks + r.clicks,
      spend: a.spend + r.spend,
      gst: a.gst + r.gst_amount,
      spendGst: a.spendGst + (r.spend_with_gst ?? r.spend + r.gst_amount),
      revenue: a.revenue + r.revenue,
      actual: a.actual + r.actual_revenue,
      margin: a.margin + r.margin,
    }),
    { clicks: 0, spend: 0, gst: 0, spendGst: 0, revenue: 0, actual: 0, margin: 0 }
  );

  const num = (k) => (
    <Input
      type="number"
      data-testid={`monthly-${k}-input`}
      className="h-9 w-28 border-slate-300 bg-white text-right"
      value={draft[k] ?? 0}
      onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
    />
  );

  return (
    <div data-testid="monthly-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Month-wise Roll-up
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">
            Monthly Sheet
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-600">
            Built automatically from your daily sheet per client and campaign. Edit any month to
            override the numbers, or import an Excel file to set them in bulk.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-testid="monthly-export-button"
            className="rounded-md border-slate-300 active:scale-95"
            onClick={exportXlsx}
          >
            <Download className="mr-2 h-4 w-4" /> Export Excel
          </Button>
          {can("monthly", "edit") && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                data-testid="monthly-import-input"
                onChange={(e) => importXlsx(e.target.files?.[0])}
              />
              <Button
                data-testid="monthly-import-button"
                className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="mr-2 h-4 w-4" /> Import Excel
              </Button>
            </>
          )}
        </div>
      </div>

      <div
        className="mb-8 grid grid-cols-2 gap-3 border border-slate-200 bg-slate-50 p-4 md:grid-cols-4"
        data-testid="monthly-filters"
      >
        <Field label="From month">
          <Input
            type="month"
            data-testid="monthly-from-input"
            className="h-9 border-slate-300 bg-white"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Field>
        <Field label="To month">
          <Input
            type="month"
            data-testid="monthly-to-input"
            className="h-9 border-slate-300 bg-white"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </Field>
        <Field label="Client">
          <Select value={client} onValueChange={setClient}>
            <SelectTrigger data-testid="monthly-client-filter" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="all">All Clients</SelectItem>
              {masters.clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Campaign">
          <Select value={campaign} onValueChange={setCampaign}>
            <SelectTrigger data-testid="monthly-campaign-filter" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="all">All Campaigns</SelectItem>
              {masters.campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full min-w-[1350px] text-sm" data-testid="monthly-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              <th className="px-3 py-3">Month</th>
              <th className="px-3 py-3">Campaign</th>
              <th className="px-3 py-3">Vertical</th>
              <th className="px-3 py-3">Client Name</th>
              <th className="px-3 py-3 text-right">Clicks</th>
              <th className="px-3 py-3 text-right">CPC</th>
              <th className="px-3 py-3 text-right">Spent</th>
              <th className="px-3 py-3 text-right">GST (18% of Spent)</th>
              <th className="px-3 py-3 text-right">Spent + GST</th>
              <th className="px-3 py-3 text-right">Revenue</th>
              <th className="px-3 py-3 text-right">Actual Revenue</th>
              <th className="px-3 py-3 text-right">Margin</th>
              <th className="px-3 py-3">Account Manager</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) =>
              editing === r.key ? (
                <tr key={r.key} className="bg-slate-50" data-testid="monthly-edit-row">
                  <td className="px-3 py-2 font-medium">{r.period}</td>
                  <td className="px-3 py-2">{r.campaign}</td>
                  <td className="px-3 py-2">{r.vertical}</td>
                  <td className="px-3 py-2">{r.client}</td>
                  <td className="px-2 py-2">{num("clicks")}</td>
                  <td className="px-3 py-2 text-right text-slate-400">auto</td>
                  <td className="px-2 py-2">{num("spend")}</td>
                  <td className="px-3 py-2 text-right text-slate-400">auto</td>
                  <td className="px-3 py-2 text-right text-slate-400">auto</td>
                  <td className="px-2 py-2">{num("revenue")}</td>
                  <td className="px-3 py-2 text-right text-slate-400">auto</td>
                  <td className="px-3 py-2 text-right text-slate-400">auto</td>
                  <td className="px-3 py-2">{r.manager}</td>
                  <td className="px-2 py-2">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        data-testid="monthly-save-button"
                        className="h-9 w-9 rounded-md bg-slate-900 active:scale-95"
                        onClick={() => save(r)}
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        data-testid="monthly-cancel-button"
                        className="h-9 w-9 rounded-md border-slate-300"
                        onClick={() => setEditing(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={r.key} className="transition-colors hover:bg-slate-50" data-testid={`monthly-row-${r.key}`}>
                  <td className="px-3 py-2 font-medium">
                    {r.period}
                    {r.overridden && (
                      <span className="ml-2 text-[10px] font-bold uppercase text-amber-600">edited</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.campaign}</td>
                  <td className="px-3 py-2">{r.vertical}</td>
                  <td className="px-3 py-2 font-semibold">{r.client}</td>
                  <td className="px-3 py-2 text-right">{r.clicks.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{money(r.cpc, r.currency)}</td>
                  <td className="px-3 py-2 text-right">{money(r.spend, r.currency)}</td>
                  <td className="px-3 py-2 text-right">{money(r.gst_amount, r.currency)}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {money(r.spend_with_gst ?? r.spend + r.gst_amount, r.currency)}
                  </td>
                  <td className="px-3 py-2 text-right">{money(r.revenue, r.currency)}</td>
                  <td className="px-3 py-2 text-right">{money(r.actual_revenue, r.currency)}</td>
                  <td
                    className={`px-3 py-2 text-right font-bold ${
                      r.margin >= 0 ? "text-emerald-600" : "text-red-600"
                    }`}
                  >
                    {money(r.margin, r.currency)}
                    <span className="ml-1 text-xs font-normal text-slate-500">({r.margin_percent}%)</span>
                  </td>
                  <td className="px-3 py-2">{r.manager}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {can("monthly", "edit") && (
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`edit-monthly-${r.key}`}
                          className="h-8 w-8"
                          onClick={() => {
                            setEditing(r.key);
                            setDraft({
                              clicks: r.clicks,
                              spend: r.spend,
                              gst_amount: r.gst_amount,
                              revenue: r.revenue,
                              notes: r.notes || "",
                            });
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {can("monthly", "edit") && r.overridden && (
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`reset-monthly-${r.key}`}
                          className="h-8 w-8 text-amber-600 hover:bg-amber-50"
                          onClick={() => reset(r)}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            )}
            {rows.length === 0 && (
              <tr>
                <td colSpan={14} className="px-3 py-10 text-center text-slate-500">
                  No data for these months yet.
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-900 bg-slate-50 font-bold" data-testid="monthly-totals">
                <td className="px-3 py-3" colSpan={4}>
                  Total · {rows.length} rows
                </td>
                <td className="px-3 py-3 text-right">{totals.clicks.toLocaleString()}</td>
                <td className="px-3 py-3" />
                <td className="px-3 py-3 text-right">{money(totals.spend)}</td>
                <td className="px-3 py-3 text-right">{money(totals.gst)}</td>
                <td className="px-3 py-3 text-right">{money(totals.spendGst)}</td>
                <td className="px-3 py-3 text-right">{money(totals.revenue)}</td>
                <td className="px-3 py-3 text-right">{money(totals.actual)}</td>
                <td className={`px-3 py-3 text-right ${totals.margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {money(totals.margin)}
                </td>
                <td className="px-3 py-3" colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
